import { ErrorMessage, Form, Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { Button, CloseButton, Table, Form as frm } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'
import * as Yup from 'yup'
import DeleteIcon from '../../../components/AllSvgIcon/DeleteIcon'
import EditIcon from '../../../components/AllSvgIcon/EditIcon'
import PlusIcon from '../../../components/AllSvgIcon/PlusIcon'
import HKBadge from '../../../components/Badges'
import FormikControl from '../../../components/FormikControl'
import IpFiletype from '../../../components/IpFiletype'
import ReactSelect from '../../../components/ReactSelect'
import SellerModal from '../../../components/SellerModal'
import TextError from '../../../components/TextError'
import {
  encodeId,
  focusInput,
  prepareNotificationData,
  showToast
} from '../../../lib/AllGlobalFunction'
import { _status_, isInventoryModel } from '../../../lib/AllStaticVariables'
import axiosProvider from '../../../lib/AxiosProvider'
import {
  _alphaNumericRegex_,
  _pincodeRegex_,
  _tradeNameRegex_
} from '../../../lib/Regex'
import { _SwalDelete, _exception } from '../../../lib/exceptionMessage'
import IpCheckbox from '../../../components/IpCheckbox'

const GSTInfo = ({
  loading,
  setLoading,
  initialValues,
  setInitialValues,
  modalShow,
  setModalShow,
  isModalRequired,
  fetchData,
  toast,
  setToast,
  initValues
}) => {
  const [allState, setAllState] = useImmer({
    country: [],
    city: [],
    state: [],
    gstDetails: []
  })
  const { userInfo } = useSelector((state) => state?.user)
  const location = useLocation()
  const SUPPORTED_FORMATS = [
    'image/jpg',
    'image/jpeg',
    'image/png',
    'application/pdf'
  ]

  const csv = async (countryId = null, stateId = null) => {
    if (!allState?.country?.length) {
      const response = await axiosProvider({
        method: 'GET',
        endpoint: 'Country/Search',
        queryString: `?pageindex=0&PageSize=0`
      })
      if (response?.status === 200) {
        setAllState((draft) => {
          draft.country = response?.data?.data
        })
      }
    }

    if (countryId) {
      const response = await axiosProvider({
        method: 'GET',
        endpoint: 'State/ByCountryId',
        queryString: `?id=${countryId}`
      })

      if (response?.status === 200) {
        setAllState((draft) => {
          draft.state = response?.data?.data
        })
      }
    }

    if (stateId) {
      const response = await axiosProvider({
        method: 'GET',
        endpoint: 'City/ByStateId',
        queryString: `?id=${stateId}`
      })

      if (response?.status === 200) {
        setAllState((draft) => {
          draft.city = response?.data?.data
        })
      }
    }
  }

  const handleDelete = async (id) => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: 'DELETE',
        endpoint: `seller/GSTInfo?id=${id}`
      })
      setLoading(false)
      if (response?.data?.code === 200) {
        setInitialValues({
          ...initialValues,
          gstInfo: initialValues?.gstInfo?.filter((x) => x.id !== id)
        })
      }
      showToast(toast, setToast, response)
    } catch {
      setLoading(false)
      showToast(toast, setToast, {
        data: {
          message: _exception?.message,
          code: 204
        }
      })
    }
  }

  const validationSchema = Yup.object().shape(
    {
      gstNo: Yup.string().required('Please Enter Tax Number'),
      fileName: Yup.string().when('fileName', {
        is: (value) => value?.name,
        then: (schema) =>
          schema
            .test(
              'fileFormat',
              'File format is not supported, Please use .jpg/.png/.jpeg/.pdf format support',
              (value) => value && SUPPORTED_FORMATS.includes(value.type)
            )
            .test('fileSize', 'File must be less than 2MB', (value) => {
              return value !== undefined && value && value.size <= 2000000
            }),
        otherwise: (schema) => schema.nullable()
      }),
      legalName: Yup.string().required('Please enter Legal Name'),
      tradeName: Yup.string().required('Please enter Trade Name'),
      gstType: Yup.string()
        .test(
          'nonull',
          'Please select GST Type',
          (value) => value !== 'undefined'
        )
        .required('Please select GST Type'),
      registeredAddressLine1: Yup.string().required(
        'Please enter Registered Address Line 1'
      ),
      registeredAddressLine2: Yup.string().required(
        'Please enter Registered Address Line 2'
      ),
      registeredPincode: Yup.string()
        .required('Pincode is required')
        .matches(_pincodeRegex_, 'Pincode must be a 6-digit number'),
      registeredCountryId: Yup.string()
        .test(
          'nonull',
          'Please select country',
          (value) => value !== 'undefined'
        )
        .required('Please select country'),
      registeredStateId: Yup.string()
        .test('nonull', 'Please select state', (value) => value !== 'undefined')
        .required('Please select state'),
      registeredCityId: Yup.string()
        .test('nonull', 'Please select city', (value) => value !== 'undefined')
        .required('Please select city'),
      status: Yup.string()
        .test(
          'nonull',
          'Please select status',
          (value) => value !== 'undefined'
        )
        .required('Please select status')
    },
    ['fileName', 'fileName']
  )

  const onSubmit = async (values) => {
    try {
      let dataofForm = {
        GSTNo: values.gstNo,
        GSTDoc: values?.fileName?.name
          ? values?.fileName?.name
          : values?.gstDoc,
        FileName: values?.fileName,
        LegalName: values?.legalName,
        TradeName: values?.tradeName,
        GSTType: values?.gstType,
        RegisteredAddressLine1: values?.registeredAddressLine1,
        RegisteredAddressLine2: values?.registeredAddressLine2,
        RegisteredLandmark: values?.registeredLandmark,
        RegisteredPincode: values?.registeredPincode,
        RegisteredStateId: values?.registeredStateId,
        RegisteredCityId: values?.registeredCityId,
        RegisteredCountryId: values?.registeredCountryId,
        TCSNo: values?.tcsNo,
        Status: values?.status,
        UserID: values?.userID,
        IsHeadOffice: values?.IsHeadOffice ? values?.IsHeadOffice : false
      }

      if (values?.id) {
        dataofForm = { ...dataofForm, Id: values?.id }
      }

      const submitFormData = new FormData()

      const keys = Object.keys(dataofForm)

      keys.forEach((key) => {
        submitFormData.append(key, dataofForm[key])
      })

      setLoading(true)
      const response = await axiosProvider({
        method: values?.id ? 'PUT' : 'POST',
        endpoint: 'seller/GSTInfo',
        data: submitFormData,
        logData: values,
        location: location?.pathname,
        userId: userInfo?.userId,
        oldData: initialValues?.gstInfo
      })
      setLoading(false)
      if (response?.data?.code === 200) {
        if (!values?.id && !initialValues?.allWarehouse) {
          setModalShow((draft) => {
            draft.createSeller = false
            draft.basicInfo = false
            draft.gstInfo = false
            draft.warehouse = true
          })
          setInitialValues({
            ...initialValues,
            warehouse: {
              ...initialValues?.warehouse,
              userID: values?.userID,
              gstNo: values?.gstNo,
              gstInfoId: response?.data?.data
            }
          })
        } else {
          const response = await axiosProvider({
            method: 'GET',
            endpoint: `seller/GSTInfo/byUserId?userId=${initialValues?.gstInfo?.userID}&status=Active`
          })

          if (response?.status === 200 && Array.isArray(response?.data?.data)) {
            setInitialValues({
              ...initialValues,
              gstInfo: response?.data?.data
            })
          } else {
            setModalShow((draft) => {
              draft.gstInfo = false
            })
          }
        }
        axiosProvider({
          endpoint: 'Notification',
          method: 'POST',
          data: prepareNotificationData({
            reciverId: values?.userID,
            userId: userInfo?.userId,
            userType: userInfo?.userType,
            notificationTitle: `GST: ${values?.legalName} ${
              values?.id ? 'updated gst successfully' : 'added gst successfully'
            }`,
            notificationDescription: `Created by ${userInfo?.fullName}`,
            url: `/manage-seller/seller-details/${encodeId(values?.userID)}`,
            notifcationsof: 'Seller'
          })
        })
        if (fetchData) {
          fetchData()
        }
        showToast(toast, setToast, response)
      }
    } catch {
      setLoading(false)

      showToast(toast, setToast, {
        data: {
          message: _exception?.message,
          code: 204
        }
      })
    }
  }

  const renderComponent = () => {
    if (!Array.isArray(initialValues?.gstInfo)) {
      return (
        <Formik
          enableReinitialize
          initialValues={initialValues?.gstInfo}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ values, setFieldValue, validateForm }) => (
            <Form id='gstInfo' className='add_seller_form'>
              <div className='row'>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <FormikControl
                      isRequired
                      control='input'
                      label='Tax No'
                      id='gstNo'
                      type='text'
                      name='gstNo'
                      placeholder='Tax No'
                      maxLength='15'
                      onBlur={(e) => {
                        let fieldName = e?.target?.name
                        setFieldValue(fieldName, values[fieldName]?.trim())
                      }}
                    />
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    {values?.gstDoc ? (
                      <frm.Group className='mb-3' controlId=''>
                        <frm.Label className='required'>Tax Document</frm.Label>
                        <div className='d-flex position-relative'>
                          <frm.Control
                            placeholder='Tax Document'
                            aria-label='Tax Document'
                            type='text'
                            disabled
                            value={values?.gstDoc}
                            name='gstDoc'
                            aria-describedby='basic-addon2'
                          />
                          <CloseButton
                            className='position-absolute top-50 end-0 translate-middle'
                            id='button-addon2'
                            onClick={() => {
                              setFieldValue('gstDoc', '')
                              setFieldValue('fileName', '')
                            }}
                          />
                        </div>
                      </frm.Group>
                    ) : (
                      <IpFiletype
                        labelClass='required'
                        filelbtext='Tax Document'
                        onChange={(e) => {
                          setFieldValue('fileName', e?.target?.files[0])
                          setFieldValue('gstDoc', e?.target?.files[0]?.name)
                        }}
                      />
                    )}
                    <ErrorMessage name='fileName' component={TextError} />
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <FormikControl
                      isRequired
                      control='input'
                      label='Legal Name'
                      id='legalName'
                      type='text'
                      name='legalName'
                      placeholder='Legal Name'
                      maxLength={50}
                      onChange={(e) => {
                        const inputValue = e?.target?.value
                        const isValid = _tradeNameRegex_.test(inputValue)
                        const fieldName = e?.target?.name
                        if (isValid || !inputValue)
                          setFieldValue([fieldName], inputValue)
                      }}
                      onBlur={(e) => {
                        let fieldName = e?.target?.name
                        setFieldValue(fieldName, values[fieldName]?.trim())
                      }}
                    />
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <FormikControl
                      isRequired
                      control='input'
                      label='Trade Name'
                      id='tradeName'
                      type='text'
                      name='tradeName'
                      placeholder='Trade Name'
                      maxLength={50}
                      onChange={(e) => {
                        const inputValue = e?.target?.value
                        const isValid = _tradeNameRegex_.test(inputValue)
                        const fieldName = e?.target?.name
                        if (isValid || !inputValue)
                          setFieldValue([fieldName], inputValue)
                      }}
                      onBlur={(e) => {
                        let fieldName = e?.target?.name
                        setFieldValue(fieldName, values[fieldName]?.trim())
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <label className='form-label required'>Tax Type</label>
                    <ReactSelect
                      id='gstType'
                      name='gstType'
                      value={
                        values?.gstType && {
                          value: values?.gstType,
                          label: values?.gstType
                        }
                      }
                      options={[
                        {
                          label: 'Regular',
                          value: 'Regular'
                        },
                        {
                          label: 'Composite',
                          value: 'Composite'
                        }
                      ]}
                      onChange={(e) => {
                        if (e) {
                          setFieldValue('gstType', e?.value)
                        }
                      }}
                    />
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <FormikControl
                      isRequired
                      control='input'
                      label='Registered Address Line 1'
                      id='registeredAddressLine1'
                      type='text'
                      name='registeredAddressLine1'
                      placeholder='Registered Address Line 1'
                      onBlur={(e) => {
                        let fieldName = e?.target?.name
                        setFieldValue(fieldName, values[fieldName]?.trim())
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <FormikControl
                      isRequired
                      control='input'
                      label='Registered Address Line 2'
                      id='registeredAddressLine2'
                      type='text'
                      name='registeredAddressLine2'
                      placeholder='Registered Address Line 2'
                      onBlur={(e) => {
                        let fieldName = e?.target?.name
                        setFieldValue(fieldName, values[fieldName]?.trim())
                      }}
                    />
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <FormikControl
                      control='input'
                      label='Registered Landmark'
                      id='registeredLandmark'
                      type='text'
                      name='registeredLandmark'
                      placeholder='Registered Landmark'
                      onBlur={(e) => {
                        let fieldName = e?.target?.name
                        setFieldValue(fieldName, values[fieldName]?.trim())
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <FormikControl
                      isRequired
                      control='input'
                      label='Registered Pincode'
                      id='registeredPincode'
                      type='text'
                      name='registeredPincode'
                      maxLength='6'
                      onBlur={async (e) => {
                        let fieldName = e?.target?.name
                        let value = values[fieldName]?.trim()
                        setFieldValue(fieldName, value)
                        setFieldValue('registeredCountryId', null)
                        setFieldValue('registeredStateId', null)
                        setFieldValue('registeredCityId', null)
                        if (value?.length === 6) {
                          const response = await axiosProvider({
                            method: 'GET',
                            endpoint: `Delivery/byPincode?pincode=${value}`
                          })
                          if (response?.status === 200) {
                            if (response?.data?.data?.countryID) {
                              let { data } = response?.data

                              if (data?.countryID) {
                                csv(data?.countryID)
                              }

                              if (data?.stateID) {
                                csv(null, data?.stateID)
                              }

                              setFieldValue(
                                'registeredCountryId',
                                data?.countryID
                              )
                              setFieldValue('countryName', data?.countryName)

                              setFieldValue('registeredStateId', data?.stateID)
                              setFieldValue('stateName', data?.stateName)

                              setFieldValue('registeredCityId', data?.cityID)
                              setFieldValue('cityName', data?.cityName)
                            } else {
                              if (!allState?.country?.length) {
                                csv()
                              }
                              setAllState((draft) => {
                                draft.state = []
                                draft.city = []
                              })
                            }
                          }
                        }
                      }}
                      onChange={(event) => {
                        const inputValue = event.target.value
                        const regex = /^[0-9\b]+$/
                        if (inputValue === '' || regex.test(inputValue)) {
                          setFieldValue('registeredPincode', inputValue)
                        }
                      }}
                      placeholder='Registered Pincode'
                    />
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <label className='form-label required'>
                      Registered Country
                    </label>
                    <ReactSelect
                      id='registeredCountryId'
                      name='registeredCountryId'
                      value={
                        values?.registeredCountryId && {
                          value: values?.registeredCountryId,
                          label: values?.countryName
                        }
                      }
                      placeholder='Registered Country'
                      options={
                        allState?.country
                          ? allState?.country?.map(({ id, name }) => ({
                              value: id,
                              label: name
                            }))
                          : []
                      }
                      onChange={(e) => {
                        if (e) {
                          setFieldValue('registeredCountryId', e?.value)
                          setFieldValue('registeredStateId', null)
                          setFieldValue('registeredCityId', null)
                          setFieldValue('countryName', e?.label)
                          setFieldValue('stateName', '')
                          setFieldValue('cityName', '')
                          setAllState((draft) => {
                            draft.state = []
                            draft.city = []
                          })

                          csv(e?.value)
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <label className='form-label required'>
                      Registered State
                    </label>
                    <ReactSelect
                      id='registeredStateId'
                      name='registeredStateId'
                      placeholder='Registered State'
                      value={
                        values?.registeredStateId && {
                          value: values?.registeredStateId,
                          label: values?.stateName
                        }
                      }
                      options={
                        allState?.state
                          ? allState?.state?.map(({ id, name }) => ({
                              value: id,
                              label: name
                            }))
                          : []
                      }
                      onChange={(e) => {
                        if (e) {
                          setFieldValue('registeredCityId', null)
                          setFieldValue('registeredStateId', e?.value)
                          setFieldValue('stateName', e?.label)
                          setAllState((draft) => {
                            draft.city = []
                          })

                          csv(null, e?.value)
                        }
                      }}
                    />
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <label className='form-label required'>
                      Registered City
                    </label>
                    <ReactSelect
                      id='registeredCityId'
                      name='registeredCityId'
                      placeholder='Registered City'
                      value={
                        values?.registeredCityId && {
                          value: values?.registeredCityId,
                          label: values?.cityName
                        }
                      }
                      options={
                        allState?.city
                          ? allState?.city?.map(({ id, name }) => ({
                              value: id,
                              label: name
                            }))
                          : []
                      }
                      onChange={(e) => {
                        if (e) {
                          setFieldValue('registeredCityId', e?.value)
                          setFieldValue('cityName', e?.label)
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <FormikControl
                      control='input'
                      label='TCS No'
                      id='tcsNo'
                      type='text'
                      name='tcsNo'
                      placeholder='TCS No'
                      maxLength={50}
                      onChange={(e) => {
                        const inputValue = e?.target?.value
                        const isValid = _alphaNumericRegex_.test(inputValue)
                        const fieldName = e?.target?.name
                        if (isValid || !inputValue)
                          setFieldValue([fieldName], inputValue)
                      }}
                      onBlur={(e) => {
                        let fieldName = e?.target?.name
                        setFieldValue(fieldName, values[fieldName]?.trim())
                      }}
                    />
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <label className='form-label required'>Status</label>
                    <ReactSelect
                      id='status'
                      name='status'
                      value={
                        values?.status && {
                          value: values?.status,
                          label: values?.status
                        }
                      }
                      options={_status_}
                      onChange={(e) => {
                        if (e) {
                          setFieldValue('status', e?.value)
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
              {isModalRequired ? (
                <></>
              ) : (
                <div className='row justify-content-center align-items-center mt-2'>
                  <span className='input-file-wrapper w-fitcontent'>
                    <IpCheckbox
                      checked={values?.IsHeadOffice ? true : false}
                      checkboxLabel={'Is Head Office'}
                      checkboxid={'IsHeadOffice'}
                      value={'IsHeadOffice'}
                      changeListener={(e) => {
                        setFieldValue('IsHeadOffice', e?.checked)
                      }}
                    />
                  </span>

                  <button
                    disabled={!values?.IsHeadOffice}
                    type='submit'
                    className='btn btn-th-blue btn btn-primary w-auto'
                    onClick={() => {
                      validateForm()?.then((focusError) =>
                        focusInput(Object?.keys(focusError)?.[0])
                      )
                    }}
                  >
                    Save
                  </button>
                </div>
              )}
            </Form>
          )}
        </Formik>
      )
    } else {
      return (
        <>
          <div className='d-flex justify-content-end align-items-center mb-3 gap-3'>
            <Button
              variant='primary'
              className='d-flex align-items-center gap-2 py-1 px-2 fw-semibold btn btn-warning'
              onClick={async () => {
                let gstInfo = initValues?.gstInfo
                gstInfo = {
                  ...gstInfo,
                  userID: initialValues?.gstInfo[0]?.userID
                }

                setInitialValues({
                  ...initialValues,
                  gstInfo,
                  allGST: initialValues?.gstInfo
                })
              }}
            >
              <PlusIcon />
              Add new Tax
            </Button>
          </div>
          <Table className='align-middle table-list hr_table_seller'>
            <thead className='align-middle'>
              <tr>
                <th>Tax No</th>
                <th>Tax Type</th>
                <th>Tax Info</th>
                <th>State</th>
                <th>Created At</th>
                <th>Modified At</th>
                <th>Status</th>
                <th className='text-nowrap'>Action</th>
              </tr>
            </thead>
            <tbody>
              {initialValues?.gstInfo?.length > 0 &&
                initialValues?.gstInfo?.map((data) => (
                  <tr key={data?.id}>
                    <td>{data?.gstNo}</td>
                    <td>{data?.gstType}</td>
                    <td>
                      <div className='d-flex align-items-center gap-2 pt-1 pb-1'>
                        Legal Name : {data?.legalName}
                      </div>
                      <div className='d-flex align-items-center gap-2 pt-1 pb-1'>
                        Trade Name : {data?.tradeName}
                      </div>
                    </td>
                    <td>{data?.stateName}</td>
                    <td>{data?.createdAt}</td>
                    <td>{data?.modifiedAt}</td>
                    <td>
                      <HKBadge
                        badgesbgname={
                          data.status === 'Active' ? 'success' : 'danger'
                        }
                        badgesTxtname={data.status}
                        badgeClassname={''}
                      />
                    </td>
                    <td className='text-center'>
                      <div className='d-flex gap-2 justify-content-center'>
                        <span
                          onClick={() => {
                            setInitialValues({
                              ...initialValues,
                              gstInfo: initialValues?.gstInfo?.find(
                                (x) => x.id === data.id
                              ),
                              allGst: initialValues?.gstInfo
                            })
                          }}
                        >
                          <EditIcon bg={'bg'} />
                        </span>
                        <span
                          onClick={() => {
                            Swal.fire({
                              title: _SwalDelete.title,
                              text: _SwalDelete.text,
                              icon: _SwalDelete.icon,
                              showCancelButton: _SwalDelete.showCancelButton,
                              confirmButtonColor:
                                _SwalDelete.confirmButtonColor,
                              cancelButtonColor: _SwalDelete.cancelButtonColor,
                              confirmButtonText: _SwalDelete.confirmButtonText,
                              cancelButtonText: _SwalDelete.cancelButtonText
                            }).then((result) => {
                              if (result.isConfirmed) {
                                handleDelete(data.id)
                              } else if (result.isDenied) {
                              }
                            })
                          }}
                        >
                          <DeleteIcon bg={'bg'} />
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </>
      )
    }
  }

  const fetchGSTData = async () => {
    const response = await axiosProvider({
      method: 'GET',
      endpoint: `seller/GSTInfo/byUserId?userId=${
        initialValues?.gstInfo?.userID
          ? initialValues?.gstInfo?.userID
          : initialValues?.gstInfo[0]?.userID
      }`
    })

    if (response?.status === 200) {
      let apiResponse = response?.data?.data
      let gstInfo = {
        ...initialValues?.gstInfo
      }
      if (Array.isArray(apiResponse)) {
        if (apiResponse?.length !== 0) {
          gstInfo = apiResponse
        }
      } else if (typeof apiResponse === 'object' && apiResponse !== null) {
        gstInfo = apiResponse
      }
      setInitialValues({
        ...initialValues,
        gstInfo
      })
      setModalShow((draft) => {
        draft.gstInfo = true
      })
    }
  }

  useEffect(() => {
    if (isInventoryModel) {
      fetchGSTData()
    }
  }, [])

  useEffect(() => {
    csv()
    if (initialValues?.gstInfo?.registeredCountryId) {
      csv(initialValues?.gstInfo?.registeredCountryId)
    }

    if (initialValues?.gstInfo?.registeredStateId) {
      csv(null, initialValues?.gstInfo?.registeredStateId)
    }
  }, [])

  return isModalRequired ? (
    <SellerModal
      show={modalShow?.gstInfo}
      modalsize={'xl'}
      modalheaderclass={''}
      onHide={() =>
        setModalShow((draft) => {
          draft.gstInfo = false
        })
      }
      btnclosetext={''}
      closebtnvariant={''}
      backdrop={'static'}
      submitbtnclass={!Array.isArray(initialValues?.gstInfo) ? '' : 'd-none'}
      buttonclass={'justify-content-start'}
      formbuttonid={'gstInfo'}
      submitname={'Save'}
      modalclass={'create_seller'}
      modalheader={
        !initialValues?.gstInfo?.id && (
          <>
            <span
              onClick={() => {
                setModalShow((draft) => {
                  draft.basicInfo = true
                  draft.gstInfo = false
                })
              }}
              className='text-decoration-none text-black fs-4 d-flex align-items-center gap-1 fw-semibold text-capitalize'
            >
              <i className='m-icon m-icon--arrow_back co-das'></i>
            </span>

            <div className='ml-auto position-relative'>
              <ul id='progressbar'>
                <li className='completed' id='account'>
                  <i className='m-icon m-icon--create_account active'></i>
                  <strong>Create Account</strong>
                </li>
                <li className='completed' id='personal'>
                  <i className='m-icon m-icon--kyc_info active'></i>
                  <strong>KYC Info</strong>
                </li>
                <li className='active' id='payment'>
                  <i className='m-icon m-icon--gst_info active'></i>
                  <strong>Tax Info</strong>
                </li>
                <li className='' id='confirm'>
                  <i className='m-icon m-icon--warehouse'></i>
                  <strong>Pickup Address</strong>
                </li>
              </ul>
            </div>
          </>
        )
      }
    >
      {renderComponent()}
    </SellerModal>
  ) : (
    <>{renderComponent()}</>
  )
}

export default GSTInfo
